import { Box, Grid, Typography } from "@mui/material";
import { GridApi, RowSelectedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/TextFieldSearchFilter";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import type { PartTypeMetricLimits } from "../models/metricLimits.models";
import { MetricLimitsDetails } from "./MetricLimitsDetails";
import { useTableData } from "../hooks/useTableData";

interface Props {
  title: string;
  partTypes: PartTypeMetricLimits[];
  isLoading: boolean;
  parentPartTypeIds?: string;
}

export const TablePartTypes = ({
  title,
  partTypes,
  isLoading,
  parentPartTypeIds,
}: Props) => {
  const queryClient = useQueryClient();
  const [filterValue, setFilterValue] = useState("");
  const [selectedPartType, setSelectedPartType] =
    useState<PartTypeMetricLimits | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { columnDefs } = useTableData();

  const onRowSelected = (event: RowSelectedEvent) => {
    if (event.node.isSelected()) {
      setSelectedPartType(event.data);
    }
  };

  const onModalHide = ({
    shouldUpdate,
    close,
  }: {
    shouldUpdate: boolean;
    close: boolean;
  }) => {
    if (close) {
      setSelectedPartType(null);
      if (gridApi) gridApi.deselectAll();
    }
    if (shouldUpdate) {
      queryClient.invalidateQueries({
        queryKey: ["metric-limits", parentPartTypeIds ?? "all"],
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      gridApi?.showLoadingOverlay();
    } else {
      gridApi?.hideOverlay();
    }
  }, [isLoading, gridApi]);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} sx={{ px: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} sx={{ mx: 2.25, my: 1.25 }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          sx={{ width: "100%", px: 1.25 }}
          spacing={2}
        >
          <Grid item md={4} xs={12}>
            <Box sx={{ height: "72vh", width: "100%" }}>
              <AgGridReact<PartTypeMetricLimits>
                rowData={partTypes || []}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                {...defaultGridOptions}
                onGridReady={(params) => setGridApi(params.api)}
                getRowId={(params) => String(params.data.PartTypeId)}
                loadingOverlayComponent={CenteredLazyLoading}
                onRowSelected={onRowSelected}
              />
            </Box>
          </Grid>
          <Grid item md={8} xs={12}>
            <Box sx={{ height: "72vh", width: "100%" }}>
              <MetricLimitsDetails
                partType={selectedPartType}
                onHide={onModalHide}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
